//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 03/01/2025
// Version       : 1
// Description   : Administrador la frecuencia en kilometros para las tareas
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import Navbar from "../../Navbar";
import { MaterialReactTable } from "material-react-table";
// TABLA EN ESPAÑOL
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Button,
  ThemeProvider,
  createTheme,
  useTheme,
  Tooltip,
} from "@mui/material";
import AgregarFrecuenciaKilometros from "./AgregarFrecuenciaKilometros";

// ICONOS
import BlockIcon from "@mui/icons-material/Block";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";

import CambiarEstadoFrecuenciaKMTarea from "./CambiarEstadoFrecuenciaKMTarea";
import { logEventos } from "../../../services/services";

function FrecuenciaKilometros() {
  const globalTheme = useTheme();
  const [frecuenciaKM, setFrecuenciaKM] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  //*************************************************************************************************************************************/

  const obtenerFrecuenciaKM = () => {
    setIsLoading(true); // Inicio de carga
    fetch(UrlApi + "/frecuencia_kilometros")
      .then((data) => data.json())
      .then((data) => setFrecuenciaKM(data))
      .catch((error) => {
        console.error("Error al obtener frecuencia de kilómetros:", error);
      })
      .finally(() => {
        setIsLoading(false); // Fin de carga (éxito o error)
      });
  };

  useEffect(() => {
    obtenerFrecuenciaKM();
  }, []);

  //*************************************************************************************************************************************/

  // DIALOG PARA AGREGAR FRECUENCIAS
  const [dialogAgregar, setDialogAgregar] = useState(false);

  // Abrir diálogo al seleccionar un vehículo
  const handleOpenAgregarFrecuencia = () => {
    setDialogAgregar(true);
  };

  // Cerrar diálogo
  const handleCloseAgregarFrecuencia = () => {
    setDialogAgregar(false);
  };

  //*************************************************************************************************************************************/

  const [frecuenciaSeleccionada, setFrecuenciaSeleccionada] = useState(null);

  // DIALOG PARA CAMBIAR ESTADO DE LAS FRECUENCIAS
  const [openCambiarEstado, setOpenCambiarEstado] = useState(false);

  const handleOpenCambiarEstado = (row) => {
    setFrecuenciaSeleccionada(row);
    setOpenCambiarEstado(true);
  };

  const handleCloseCambiarEstado = () => {
    setOpenCambiarEstado(false);
  };

  //*************************************************************************************************************************************/

  // ----------- TABLA ---------------
  const customLocalization = {
    ...MRT_Localization_ES,
    body: {
      ...MRT_Localization_ES.body,
      noData: "No hay registros para mostrar",
    },
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "id_frecuencia_km",
        header: "ID",
        size: 150,
      },
      {
        accessorKey: "valor_km",
        header: "Valor (km)",
        size: 150,
      },
    ],
    []
  );

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: { pageIndex: 0, pageSize: 10 },
    sorting: [{ id: "valor_km", desc: false }], // Orden ascendente por "valor_km"
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
    },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { backgroundColor: "#ffffff" },
    variant: "outlined",
    size: "small",
  };

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "1rem",
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.8rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  //   ---------------------------------------------------

  // Función para actualizar las tareas luego de que se guarde una tarea
  const handleFrecuenciaGuardada = () => {
    obtenerFrecuenciaKM(); // Vuelve a obtener los datos actualizados
  };

  useEffect(() => {
    logEventos(
      "acceso al menú Frecuencia de Tareas (km)",
      "Utilización de Vehículos",
      "Frecuencia de Tareas (km)",
      localStorage.getItem("user")
    );
  }, []);
  
  return (
    <>
      <Navbar />
      <Box
        sx={{
          marginTop: "20px",
          marginBottom: "25px",
          marginLeft: "40px",
          marginRight: "40px",
        }}
      >
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={columns}
            data={frecuenciaKM}
            initialState={initialState}
            positionActionsColumn="last"
            autoResetPageIndex={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={true}
            enableColumnFilters={false} // Desactiva filtros por columna
            enableHiding={false}
            localization={customLocalization}
            enableRowSelection={false}
            enableRowActions={true}
            muiTableHeadCellProps={tableHeadCellProps}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            muiTableBodyCellProps={({}) => ({
              align: "left",
              sx: {
                color: "black",
                borderColor: "black",
              },
            })}
            muiTableBodyRowProps={({ row }) => {
              const isEstadoActivo = row.original.estado === 1;

              // Paletas para activos (estado = 1) e inactivos (estado != 1)
              const activeColor = ["#a4f0a8"];
              const inactiveColor = ["#f5bbbbff"];

              // Colores al pasar el mouse
              const activeHover = "#d1f0d1";
              const inactiveHover = "#f3d4d4ff";

              const bgColor = isEstadoActivo ? activeColor : inactiveColor;
              const hoverColor = isEstadoActivo ? activeHover : inactiveHover;

              return {
                sx: {
                  backgroundColor: bgColor,
                  color: "black",
                  borderColor: "black",
                  transition: "background-color 0.2s ease-in-out",
                  "&:hover": isLoading
                    ? {
                        backgroundColor: bgColor + " !important",
                        "& .MuiTableCell-root": {
                          backgroundColor: bgColor + " !important",
                        },
                      }
                    : {
                        backgroundColor: hoverColor,
                        "& .MuiTableCell-root": {
                          backgroundColor: "inherit !important",
                        },
                      },
                },
              };
            }}
            renderTopToolbarCustomActions={() => (
              <>
                <Typography variant="poster" fontSize={26}>
                  Frecuencia de Tareas para Vehículos
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  style={{ marginTop: "5px" }}
                  startIcon={<AddBoxOutlinedIcon />}
                  onClick={handleOpenAgregarFrecuencia} // Abre el diálogo
                >
                  Agregar Frecuencia
                </Button>
              </>
            )}
            renderRowActions={({ row }) => (
              <>
                <Tooltip
                  arrow
                  placement="left"
                  disableInteractive
                  title={
                    row.original.estado === 1
                      ? "Anular Frecuencia"
                      : "Activar Frecuencia"
                  }
                >
                  <IconButton
                    onClick={() => handleOpenCambiarEstado(row.original)}
                  >
                    <BlockIcon
                      sx={{
                        color: row.original.estado === 1 ? "red" : "green",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </>
            )}
          />
        </ThemeProvider>
      </Box>

      <Dialog
        fullWidth
        maxWidth={"xs"}
        open={dialogAgregar}
        onClose={handleCloseAgregarFrecuencia}
      >
        <AgregarFrecuenciaKilometros
          handleCloseAgregarFrecuencia={handleCloseAgregarFrecuencia}
          onFrecuenciaGuardada={handleFrecuenciaGuardada} // Recargar tabla al guardar
        />
      </Dialog>

      {/* CAMBIAR ESTADO TAREA */}
      <Dialog
        fullWidth
        maxWidth={"xs"}
        open={openCambiarEstado}
        onClose={handleCloseCambiarEstado}
      >
        {frecuenciaSeleccionada && (
          <CambiarEstadoFrecuenciaKMTarea
            frecuencia={frecuenciaSeleccionada}
            onClose={handleCloseCambiarEstado} // Función para cerrar el diálogo
            onGuardarEstado={handleFrecuenciaGuardada} // Recargar tabla al guardar
          />
        )}
      </Dialog>
    </>
  );
}

export default FrecuenciaKilometros;
